import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "drawer", "player", "rewards", "share", "error", "syncStatus"]
  static values = { selected: Number, base: String, sync: String }
  connect() {
    this.ids = Array.from(this.element.querySelectorAll("[data-video-id]"), el => Number(el.dataset.videoId))
    if (this.selectedValue > 0) this.load(this.selectedValue)
    this.beforeCache = () => this.close()
    document.addEventListener("turbo:before-cache", this.beforeCache)
    this.syncRewards()
  }
  disconnect() {
    this.abort?.abort()
    this.syncAbort?.abort()
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    if (this.hasDialogTarget) this.dialogTarget.close()
  }
  open(event) { event.preventDefault(); this.opener = event.currentTarget; this.load(Number(event.currentTarget.dataset.videoId)) }
  async load(id) {
    this.abort?.abort()
    this.abort = new AbortController()
    const signal = this.abort.signal
    this.currentId = id
    this.rewardAmount = null
    this.shareUrl = null
    this.drawerTarget.innerHTML = '<header class="hv-drawer-header"><h2>Loading video…</h2><button type="button" class="admin-button secondary" data-action="admin-host-videos#close">Close</button></header><div class="hv-drawer-body"><div role="alert" data-admin-host-videos-target="error" hidden></div></div>'
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    const query = new URLSearchParams(window.location.search)
    query.set("drawer", "1")
    try {
      const response = await fetch(`${this.baseValue}/${id}?${query}`, {signal, headers: {Accept: "text/html"}})
      if (!response.ok || response.redirected) throw new Error("Video details could not be loaded. Refresh the page and sign in again if needed.")
      const html = await response.text()
      if (signal.aborted) return
      this.drawerTarget.innerHTML = html
      const index = this.ids.indexOf(id)
      this.drawerTarget.querySelector('[data-action="admin-host-videos#previous"]').disabled = index <= 0
      this.drawerTarget.querySelector('[data-action="admin-host-videos#next"]').disabled = index < 0 || index >= this.ids.length - 1
      this.drawerTarget.querySelector('[aria-label="Close video"]')?.focus()
      this.loadMedia(signal)
      this.reloadRewards()
    } catch (error) { if (error.name !== "AbortError") this.showError(error.message) }
  }
  close() { this.abort?.abort(); if (this.hasDialogTarget) this.dialogTarget.close() }
  closed() { this.abort?.abort(); this.drawerTarget.innerHTML = ""; this.opener?.focus(); this.refreshMatches() }
  backdrop(event) { if (event.target === this.dialogTarget) { const box = this.dialogTarget.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right) this.close() } }
  previous() { this.step(-1) }
  next() { this.step(1) }
  step(offset) { const index = this.ids.indexOf(this.currentId); if (index >= 0 && this.ids[index + offset]) this.load(this.ids[index + offset]) }
  async json(url, options = {}) {
    const response = await fetch(url, {...options, headers: {Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", ...options.headers}})
    if (response.redirected) throw new Error("Your admin session has expired. Refresh and sign in again.")
    let body
    try { body = await response.json() } catch (_) { throw new Error("The request could not be confirmed. Refresh the video and check its status.") }
    if (!response.ok) {
      const error = new Error(body.error || "The request could not be completed.")
      error.code = body.code
      throw error
    }
    return body
  }
  async syncRewards() {
    if (!this.hasSyncValue || this.syncing) return
    this.syncing = true
    this.syncAbort = new AbortController()
    const signal = this.syncAbort.signal
    let checked = 0
    try {
      this.syncStatusTarget.textContent = "Matching existing Giftbit rewards by email…"
      while (!signal.aborted) {
        const result = await this.json(this.syncValue, {method: "POST", signal})
        if (result.done) break
        checked++
        if (result.matched) {
          this.matchesChanged = true
          if (this.dialogTarget.open && this.currentId === result.video_id) this.load(this.currentId)
        }
        this.syncStatusTarget.textContent = `Matching existing Giftbit rewards by email… ${checked} checked.`
      }
      if (signal.aborted) return
      this.syncStatusTarget.textContent = "Existing Giftbit rewards matched automatically by email."
    } catch (error) {
      if (error.name === "AbortError") return
      this.syncStatusTarget.textContent = `Automatic matching paused: ${error.message} Reopen Host videos to retry.`
    } finally {
      this.syncing = false
    }
    this.refreshMatches()
  }
  refreshMatches() {
    if (!this.matchesChanged || this.syncing || this.dialogTarget.open || this.syncAbort?.signal.aborted) return
    // Matching can remove the last row from the current page.
    const url = new URL(window.location.href)
    url.searchParams.delete("page")
    url.searchParams.delete("video")
    window.location.replace(url)
  }
  async loadMedia(signal) {
    if (!this.hasPlayerTarget) return
    const player = this.playerTarget
    try {
      const media = await this.json(player.dataset.url, {signal})
      if (signal.aborted) return
      const iframe = document.createElement("iframe")
      iframe.src = media.iframe
      iframe.title = "Host video"
      iframe.allow = "accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
      iframe.allowFullscreen = true
      player.replaceChildren(iframe)
      player.classList.toggle("portrait", media.portrait)
      this.shareUrl = media.share
      this.shareTarget.disabled = false
      this.shareTarget.title = media.signed ? "Playback link expires in one hour" : "Copy playback link"
    } catch (error) { if (error.name !== "AbortError") player.textContent = error.message }
  }
  async reloadRewards() {
    if (!this.hasRewardsTarget) return false
    const target = this.rewardsTarget
    const signal = this.abort.signal
    this.rewardAmount = target.querySelector('input[name="confirmation_token"]:checked')?.dataset.amount || this.rewardAmount
    target.innerHTML = '<p class="hv-muted">Loading reward details…</p>'
    try {
      const response = await fetch(target.dataset.url, {signal, headers: {Accept: "text/html"}})
      if (!response.ok || response.redirected) throw new Error("Reward history could not be loaded. Reopen the video to try again.")
      const html = await response.text()
      if (signal.aborted) return false
      target.innerHTML = html
      const selected = Array.from(target.querySelectorAll('input[name="confirmation_token"]')).find(input => input.dataset.amount === this.rewardAmount)
      if (selected) selected.checked = true
      this.selectRewardAmount()
      return true
    } catch (error) {
      if (error.name !== "AbortError") {
        target.innerHTML = '<p class="hv-notice">Reward details could not be refreshed. Try again before sending a gift card.</p><button type="button" class="admin-button secondary small" data-action="admin-host-videos#reloadRewards">Retry reward lookup</button>'
        this.showError(error.message)
      }
      return false
    }
  }
  selectRewardAmount() {
    const amount = this.rewardsTarget.querySelector('input[name="confirmation_token"]:checked')?.dataset.amount
    if (!["25", "50"].includes(amount)) return
    this.rewardAmount = amount
    this.rewardsTarget.querySelectorAll('[data-reward-amount]').forEach(label => label.textContent = amount)
  }
  async submit(event) {
    event.preventDefault()
    const form = event.currentTarget
    if (this.busy) return
    this.busy = true
    const videoId = this.currentId
    const buttons = Array.from(this.drawerTarget.querySelectorAll('button[type="submit"]'))
    buttons.forEach(button => button.disabled = true)
    try {
      const result = await this.json(form.action, {method: "POST", body: new FormData(form)})
      window.location.assign(result.url)
    } catch (error) {
      if (error.code === "confirmation_required" && this.currentId === videoId && !this.abort.signal.aborted) {
        if (await this.reloadRewards()) {
          const approval = this.rewardsTarget.querySelector('.hv-approve')
          approval?.setAttribute('open', '')
          this.showError(approval ? "Reward details refreshed. Review them and confirm again if you still want to send the gift card. No gift card was sent." : "Reward details refreshed. Check the latest reward status below. No gift card was sent.")
        }
      } else {
        this.showError(error.message)
      }
      buttons.forEach(button => button.disabled = false)
    } finally { this.busy = false }
  }
  async download(event) {
    const button = event.currentTarget
    button.disabled = true
    try {
      const result = await this.json(button.dataset.url, {method: "POST"})
      if (result.pending) this.showError(result.message)
      else window.location.assign(result.url)
    } catch (error) { this.showError(error.message) }
    finally { button.disabled = false }
  }
  async copy(event) { this.copyText(event.currentTarget.dataset.copy, event.currentTarget) }
  async share(event) { if (this.shareUrl) this.copyText(this.shareUrl, event.currentTarget) }
  async copyText(value, button) {
    try { await navigator.clipboard.writeText(value); const label = button.title; button.title = "Copied"; button.setAttribute("aria-label", "Copied"); setTimeout(() => button.title = label, 1500) }
    catch (_) { this.showError("Could not copy automatically. Select and copy the value shown in the details.") }
  }
  hideImage(event) { event.currentTarget.hidden = true }
  showError(message) { if (this.hasErrorTarget) { this.errorTarget.textContent = message; this.errorTarget.hidden = false; this.errorTarget.scrollIntoView({block: "nearest"}) } }
}
